import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { closeOverlay, setIndex, useImmersiveOverlay } from 'app/shared/halo/stores/immersiveOverlayStore';
import { ImmersiveContainer } from 'app/shared/halo/layouts/ImmersiveContainer';
import { useInfiniteListings } from 'app/shared/halo/hooks/useInfiniteListings';
import { DetailPanelOutlet, DetailPanelProvider } from 'app/shared/halo/components/ui/DetailPanel';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import { ImmersiveFilterNav } from './ImmersiveFilterNav';
import { ImmersiveCard } from './ImmersiveCard';
import * as styles from './immersiveOverlay.styles';

const SCROLL_LOCK_MS = 400;
const PREFETCH_THRESHOLD = 5;
const VIRTUALIZATION_WINDOW = 2;
const DISCRETE_WHEEL_THRESHOLD = 50;

export const ImmersiveOverlay = () => {
  const { isOpen, listing: overlayListing, index: startIndex } = useImmersiveOverlay();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const scrollLockTimerRef = useRef<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(Math.max(0, startIndex - 1));
  const activeIndexRef = useRef(activeIndex);
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = useCallback(() => {
    setIsClosing(true);
  }, []);

  const handleAnimationComplete = useCallback(() => {
    setIsClosing(false);
    closeOverlay();
  }, []);

  // Reset state when the overlay opens with a new starting card.
  // useState only initializes on mount; the component stays mounted
  // (renders null when closed) so we must sync manually.
  useEffect(() => {
    if (isOpen) {
      const idx = Math.max(0, startIndex - 1);
      activeIndexRef.current = idx;
      setActiveIndex(idx);
    }
  }, [isOpen, startIndex]);

  const isMobile = useSelector((state: RootReduxState) => state.app.device.screenWidth === 'sm');
  const currentListing = useSelector((state: RootReduxState) => state.currentListingDetails.currentListing);
  const byCoords = useSelector((state: RootReduxState) => state.listings.listingGroups.byCoords) as unknown as Array<
    Record<string, unknown>
  > | null;
  const totalListings = useSelector((state: RootReduxState) => state.listings.totalListings);

  const initialListings = byCoords && byCoords.length > 0 ? byCoords : [];
  const { allListings, fetchNextPage, isFetching, hasMore } = useInfiniteListings(initialListings, totalListings);
  const listings = allListings;

  // If byCoords is empty but we have a single listing from the overlay/Redux, show that
  const singleListing = (currentListing || overlayListing) as Record<string, unknown> | null;
  const hasListings = listings.length > 0;

  const clearScrollLockTimer = useCallback(() => {
    if (scrollLockTimerRef.current !== null) {
      window.clearTimeout(scrollLockTimerRef.current);
      scrollLockTimerRef.current = null;
    }
  }, []);

  const lockScroll = useCallback(() => {
    isScrollingRef.current = true;
    clearScrollLockTimer();
    scrollLockTimerRef.current = window.setTimeout(() => {
      isScrollingRef.current = false;
      scrollLockTimerRef.current = null;
    }, SCROLL_LOCK_MS);
  }, [clearScrollLockTimer]);

  const getPageHeight = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return 0;
    const firstPage = container.querySelector('[data-index]') as HTMLElement | null;
    if (!firstPage) return container.clientHeight;
    const style = getComputedStyle(firstPage);
    const gap = parseFloat(getComputedStyle(container).gap) || 0;
    const height = firstPage.offsetHeight + parseFloat(style.marginTop) + parseFloat(style.marginBottom) + gap;
    return height;
  }, []);

  const disableSnapForScroll = useCallback((container: HTMLDivElement) => {
    container.style.scrollSnapType = 'none';
  }, []);

  const reEnableSnap = useCallback((container: HTMLDivElement) => {
    container.style.scrollSnapType = '';
  }, []);

  const scrollToPage = useCallback(
    (pageIndex: number) => {
      const container = scrollContainerRef.current;
      if (!container || !container.scrollTo) return;

      const pageHeight = getPageHeight();
      const targetTop = pageIndex * pageHeight;

      disableSnapForScroll(container);
      container.scrollTo({ top: targetTop, behavior: 'smooth' });
    },
    [getPageHeight, disableSnapForScroll],
  );

  // Scroll to the starting card when the overlay opens
  useEffect(() => {
    if (isOpen && scrollContainerRef.current && hasListings && startIndex > 0 && scrollContainerRef.current.scrollTo) {
      const pageHeight = getPageHeight();
      const targetTop = (startIndex - 1) * pageHeight;
      scrollContainerRef.current.scrollTo({
        top: targetTop,
        behavior: 'instant' as ScrollBehavior,
      });
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track visible card and animation state with IntersectionObserver
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !hasListings) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.intersectionRatio >= 0.5) {
            const idx = Number(entry.target.getAttribute('data-index'));
            activeIndexRef.current = idx;
            setActiveIndex(idx);
            setIndex(idx + 1);
          }
        }
      },
      { root: container, threshold: [0.5] },
    );

    container.querySelectorAll('[data-index]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [isOpen, hasListings, listings.length]);

  // Prefetch next page when approaching the end
  useEffect(() => {
    if (hasMore && activeIndex >= listings.length - PREFETCH_THRESHOLD) {
      fetchNextPage();
    }
  }, [activeIndex, listings.length, hasMore, fetchNextPage]);

  // Discrete mouse wheel: intercept and navigate one card at a time.
  // Trackpad events (fractional deltaY) pass through to native scroll-snap.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !hasListings) return;

    const isDiscreteWheel = (event: WheelEvent) => {
      if (event.deltaMode !== 0) return true;
      return Number.isInteger(event.deltaY) && Math.abs(event.deltaY) >= DISCRETE_WHEEL_THRESHOLD;
    };

    const handleWheel = (e: WheelEvent) => {
      const discrete = isDiscreteWheel(e);

      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) {
        return;
      }

      // During programmatic scroll, block ALL wheel events to prevent
      // trackpad inertia from interfering with the smooth scroll animation.
      if (isScrollingRef.current) {
        e.preventDefault();
        return;
      }

      if (!discrete) {
        return;
      }

      e.preventDefault();

      const direction = e.deltaY > 0 ? 1 : -1;
      const next = activeIndexRef.current + direction;

      if (next < 0 || next >= listings.length) {
        return;
      }

      lockScroll();
      scrollToPage(next);
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [hasListings, listings.length, lockScroll, scrollToPage]);

  // Re-enable CSS scroll-snap after a programmatic scroll completes.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !hasListings) return;

    const handleScrollEnd = () => {
      if (container.style.scrollSnapType === 'none') {
        reEnableSnap(container);
      }
    };

    container.addEventListener('scrollend', handleScrollEnd, { passive: true });
    return () => {
      container.removeEventListener('scrollend', handleScrollEnd);
    };
  }, [hasListings, reEnableSnap]);

  // Arrow key navigation with scroll lock to prevent double-tap issues
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !hasListings) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;

      e.preventDefault();

      if (isScrollingRef.current) {
        return;
      }

      const next = e.key === 'ArrowDown' ? activeIndexRef.current + 1 : activeIndexRef.current - 1;

      if (next < 0 || next >= listings.length) {
        return;
      }

      lockScroll();
      scrollToPage(next);
    };

    container.addEventListener('keydown', handleKeyDown);
    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [hasListings, listings.length, lockScroll, scrollToPage]);

  useEffect(() => {
    return () => {
      clearScrollLockTimer();
    };
  }, [clearScrollLockTimer]);

  useEffect(() => {
    if (!isOpen) {
      clearScrollLockTimer();
      isScrollingRef.current = false;
      const container = scrollContainerRef.current;
      if (container) {
        container.style.scrollSnapType = '';
      }
    }
  }, [clearScrollLockTimer, isOpen]);

  // Ensure scroll container is focusable for keyboard events
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container && isOpen && hasListings) {
      container.focus({ preventScroll: true });
    }
  }, [isOpen, hasListings]);

  if (!isOpen) {
    return null;
  }

  if (hasListings) {
    return (
      <ImmersiveContainer isClosing={isClosing} onAnimationComplete={handleAnimationComplete}>
        <DetailPanelProvider isMobile={isMobile} scrollContainerRef={scrollContainerRef}>
          <ImmersiveFilterNav onClose={handleClose} />
          <div
            className={styles.immersiveScrollContainer}
            ref={scrollContainerRef}
            tabIndex={-1}
            role="list"
            aria-label="Listing cards"
          >
            {listings.map((listing, i) => {
              const id = (listing as unknown as { maloneLotIdEncoded?: string }).maloneLotIdEncoded || i;
              const isNearActive = Math.abs(i - activeIndex) <= VIRTUALIZATION_WINDOW;
              return (
                <div className={styles.immersiveScrollPage} key={id} data-index={i} role="listitem">
                  {isNearActive ? (
                    <ImmersiveCard listing={listing} animationState={i === activeIndex ? 'running' : 'idle'} />
                  ) : (
                    <div className={styles.immersiveCardPlaceholder} />
                  )}
                </div>
              );
            })}
            {isFetching && (
              <div className={styles.immersiveLoadingIndicator} role="status" aria-label="Loading more listings">
                Loading more listings…
              </div>
            )}
          </div>
          {!isMobile && <DetailPanelOutlet />}
        </DetailPanelProvider>
      </ImmersiveContainer>
    );
  }

  // Fallback: single listing (no byCoords available)
  if (singleListing) {
    return (
      <ImmersiveContainer isClosing={isClosing} onAnimationComplete={handleAnimationComplete}>
        <DetailPanelProvider isMobile={isMobile} scrollContainerRef={scrollContainerRef}>
          <ImmersiveFilterNav onClose={handleClose} />
          <ImmersiveCard listing={singleListing} animationState="running" />
          {!isMobile && <DetailPanelOutlet />}
        </DetailPanelProvider>
      </ImmersiveContainer>
    );
  }

  return null;
};
